import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { AppUser, UserRole, UserStatus } from '@/types';

interface AuthState {
  firebaseUser: User | null;
  appUser: AppUser | null;
  companyCountry: string | null;
  loading: boolean;
}

/** Dado el documento de usuario, devuelve la ruta de destino tras login/register */
export function computeLandingRoute(appUser: AppUser | null): string {
  if (!appUser) return '/login';
  if (appUser.role === 'owner') return '/owner/dashboard';
  if (appUser.status === 'pending' || (appUser.companyId == null && appUser.role !== 'owner'))
    return '/pending-approval';
  return '/app/dashboard';
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<string>;
  signUp: (email: string, password: string, displayName: string) => Promise<string>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshAppUser: () => Promise<void>;
  computeLandingRoute: (appUser: AppUser | null) => string;
  isOwner: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  canWriteCompanyData: boolean;
  canAccessCompany: (companyId: string | { id: string } | null | undefined) => boolean;
  isPending: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    appUser: null,
    companyCountry: null,
    loading: true,
  });

  const fetchAppUser = useCallback(async (uid: string): Promise<AppUser | null> => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) return null;
    const d = userDoc.data();
    const rawCompanyId = d.companyId;
    let companyId: string | null = null;
    if (typeof rawCompanyId === 'string' && rawCompanyId.trim()) {
      companyId = rawCompanyId.trim();
    } else if (rawCompanyId != null && typeof (rawCompanyId as { id?: string }).id === 'string') {
      companyId = (rawCompanyId as { id: string }).id;
    }
    return {
      uid: userDoc.id,
      email: d.email ?? '',
      displayName: d.displayName ?? '',
      companyId,
      role: (d.role as UserRole) ?? 'viewer',
      status: (d.status as UserStatus) ?? 'active',
      createdAt: d.createdAt ?? '',
    };
  }, []);

  const refreshAppUser = useCallback(async () => {
    if (!state.firebaseUser) {
      setState((s) => ({ ...s, appUser: null, companyCountry: null, loading: false }));
      return;
    }
    const appUser = await fetchAppUser(state.firebaseUser.uid);
    let companyCountry: string | null = null;
    if (appUser?.companyId) {
      const companySnap = await getDoc(doc(db, 'companies', appUser.companyId));
      companyCountry = companySnap.exists() ? (companySnap.data().country ?? null) : null;
    }
    setState((s) => ({ ...s, appUser, companyCountry, loading: false }));
  }, [state.firebaseUser, fetchAppUser]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ firebaseUser: null, appUser: null, companyCountry: null, loading: false });
        return;
      }
      const appUser = await fetchAppUser(user.uid);
      let companyCountry: string | null = null;
      if (appUser?.companyId) {
        const companySnap = await getDoc(doc(db, 'companies', appUser.companyId));
        companyCountry = companySnap.exists() ? (companySnap.data().country ?? null) : null;
      }
      setState({ firebaseUser: user, appUser, companyCountry, loading: false });
    });
    return () => unsub();
  }, [fetchAppUser]);

  const signIn = async (email: string, password: string): Promise<string> => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const appUser = await fetchAppUser(cred.user.uid);
    let companyCountry: string | null = null;
    if (appUser?.companyId) {
      const companySnap = await getDoc(doc(db, 'companies', appUser.companyId));
      companyCountry = companySnap.exists() ? (companySnap.data().country ?? null) : null;
    }
    setState({ firebaseUser: cred.user, appUser, companyCountry, loading: false });
    return computeLandingRoute(appUser);
  };

  const signUp = async (email: string, password: string, displayName: string): Promise<string> => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await setDoc(doc(db, 'users', cred.user.uid), {
        email,
        displayName: displayName || null,
        role: 'pending',
        companyId: null,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Firestore: error creando users/{uid}', err);
      throw err;
    }
    const appUser = await fetchAppUser(cred.user.uid);
    setState({ firebaseUser: cred.user, appUser, loading: false });
    return computeLandingRoute(appUser);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const isOwner = state.appUser?.role === 'owner';
  const isAdmin = state.appUser?.role === 'admin';
  const isViewer = state.appUser?.role === 'viewer';
  const canWriteCompanyData =
    state.appUser?.role === 'owner' || state.appUser?.role === 'admin' || state.appUser?.role === 'recruiter';
  const isPending =
    (state.appUser?.status === 'pending' || (state.appUser?.companyId == null && state.appUser?.role !== 'owner')) ?? false;
  const canAccessCompany = (companyId: string | { id: string } | null | undefined) => {
    if (!state.appUser) return false;
    if (state.appUser.role === 'owner') return true;
    const cid = companyId == null ? null : typeof companyId === 'string' ? companyId.trim() : (companyId as { id: string }).id;
    return state.appUser.companyId === cid;
  };

  const value: AuthContextValue = {
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshAppUser,
    computeLandingRoute,
    isOwner,
    isAdmin,
    isViewer,
    canWriteCompanyData,
    isPending,
    canAccessCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
